import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { FileText, Download, Image as ImageIcon, Film, Music, Loader2, RefreshCw, AlertTriangle, Clock, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface MessageAttachmentProps {
  attachment: {
    id: string;
    file_name: string;
    content_type: string;
    storage_path: string;
    file_size: number;
    needs_reprocess?: boolean;
    created_at?: string; // Message creation date for expiry check
    external_url?: string; // Fallback URL for Evolution API media
  };
  isOutbound: boolean;
  onRequestResend?: () => void; // Callback to request customer to resend
}

// Minimum sizes to consider media valid (not thumbnails)
const MIN_MEDIA_SIZES: Record<string, number> = {
  'image': 3000,
  'video': 10000,
  'audio': 2000,
  'document': 1000,
};

// WhatsApp media typically expires after 30 days
const MEDIA_EXPIRY_DAYS = 30;

export function MessageAttachment({ attachment, isOutbound, onRequestResend }: MessageAttachmentProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [externalUrlFailed, setExternalUrlFailed] = useState(false);
  const [storageUrlFailed, setStorageUrlFailed] = useState(false);
  const [loadAttempted, setLoadAttempted] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  // Check if we're using external URL (Evolution API) instead of storage
  const hasStoragePath = !!(attachment.storage_path && attachment.storage_path.trim() !== '');
  const hasExternalUrl = !!attachment.external_url;
  const isExternalOnly = !hasStoragePath && hasExternalUrl;
  
  // Simplified: No media available only if both sources don't exist at all
  // Fallback logic is handled in render, not in state calculation
  const noMediaAvailable = !hasStoragePath && !hasExternalUrl;

  // Reset all states when attachment props change (different message)
  useEffect(() => {
    setStorageUrlFailed(false);
    setExternalUrlFailed(false);
    setImageUrl(null);
    setLoadAttempted(false);
    setUsingFallback(false);
    setImageLoading(true);
  }, [attachment.id, attachment.storage_path, attachment.external_url]);

  // Debug logging
  useEffect(() => {
    console.log('[MessageAttachment] Props:', {
      id: attachment.id?.substring(0, 8),
      storage_path: attachment.storage_path?.substring(0, 50),
      external_url: attachment.external_url?.substring(0, 50),
      hasStoragePath,
      hasExternalUrl,
      noMediaAvailable,
    });
  }, [attachment.id, attachment.storage_path, attachment.external_url, hasStoragePath, hasExternalUrl, noMediaAvailable]);

  const isImage = attachment.content_type?.startsWith('image/');
  const isVideo = attachment.content_type?.startsWith('video/');
  const isAudio = attachment.content_type?.startsWith('audio/');
  // Só trata como PDF se o mimetype E a extensão concordarem.
  // Evita "Ver PDF" em .docx/.xlsx/.txt salvos com mimetype errado (fallback legado do webhook).
  const isPdf = attachment.content_type === 'application/pdf'
    && (attachment.file_name?.toLowerCase().endsWith('.pdf') ?? false);

  // Determine if this is a small/thumbnail file that needs reprocessing
  const getMediaType = () => {
    if (isImage) return 'image';
    if (isVideo) return 'video';
    if (isAudio) return 'audio';
    return 'document';
  };
  
  const minSize = MIN_MEDIA_SIZES[getMediaType()] || 1000;
  // Não considerar "muito pequeno" se:
  // - file_size é 0 ou desconhecido (ainda não carregou)
  // - Só temos URL externa (Evolution API não reporta tamanho consistente)
  const hasKnownSize = attachment.file_size > 0;
  const isTooSmall = hasKnownSize && !isExternalOnly && attachment.file_size < minSize;
  const needsReprocess = attachment.needs_reprocess === true && isTooSmall;
  
  // Check if media is expired (older than 30 days)
  const isMediaExpired = () => {
    if (!attachment.created_at) return false;
    const createdDate = new Date(attachment.created_at);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays > MEDIA_EXPIRY_DAYS;
  };
  
  const mediaExpired = isMediaExpired();
  const daysSinceCreation = attachment.created_at 
    ? Math.floor((new Date().getTime() - new Date(attachment.created_at).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  // Função estável para carregar imagem com fallback robusto
  const loadImage = useCallback(() => {
    setImageLoading(true);
    
    // Priority 1: Try storage_path first (permanent)
    if (hasStoragePath && !storageUrlFailed) {
      const { data } = supabase.storage
        .from('whatsapp-media')
        .getPublicUrl(attachment.storage_path);
      console.log('[MessageAttachment] Generated storage URL:', data.publicUrl);
      console.log('[MessageAttachment] Loading from storage:', attachment.storage_path);
      setImageUrl(data.publicUrl);
      setUsingFallback(false);
      return;
    }
    
    // Priority 2: Fallback to external_url (temporary)
    if (hasExternalUrl && !externalUrlFailed) {
      console.log('[MessageAttachment] Loading from external URL (fallback):', attachment.external_url?.substring(0, 50));
      setImageUrl(attachment.external_url!);
      setUsingFallback(true);
      return;
    }
    
    // No source available
    console.log('[MessageAttachment] No media source available');
    setImageLoading(false);
  }, [hasStoragePath, hasExternalUrl, storageUrlFailed, externalUrlFailed, attachment.storage_path, attachment.external_url]);

  // Handle image load error with fallback logic
  const handleImageError = useCallback(() => {
    console.error('[MessageAttachment] Failed to load image from:', imageUrl?.substring(0, 50));
    
    // If we were using storage and it failed, try external URL
    if (hasStoragePath && !storageUrlFailed && hasExternalUrl && !externalUrlFailed) {
      console.log('[MessageAttachment] Storage failed, trying external URL fallback...');
      setStorageUrlFailed(true);
      const extUrl = attachment.external_url!;
      setImageUrl(extUrl);
      setUsingFallback(true);
      return;
    }
    
    // All sources failed - clear imageUrl to show error UI
    console.log('[MessageAttachment] All sources failed, showing error UI');
    setExternalUrlFailed(true);
    setImageUrl(null);
    setImageLoading(false);
  }, [imageUrl, hasStoragePath, hasExternalUrl, storageUrlFailed, externalUrlFailed, attachment.external_url]);

  // Auto-load images when component mounts
  useEffect(() => {
    if (isImage && !imageUrl && !needsReprocess && !loadAttempted && !noMediaAvailable) {
      setLoadAttempted(true);
      loadImage();
    }
  }, [isImage, imageUrl, needsReprocess, loadAttempted, noMediaAvailable, loadImage]);

  const handleDownload = async () => {
    // For external URLs, open in new tab (can't download cross-origin)
    if (isExternalOnly) {
      window.open(attachment.external_url!, '_blank');
      return;
    }
    
    if (!hasStoragePath) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from('whatsapp-media')
        .download(attachment.storage_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      // Get the shared session name
      const { data: sharedSession, error: sessionError } = await supabase
        .from('shared_whatsapp_session')
        .select('session_name')
        .eq('is_active', true)
        .single();

      if (sessionError || !sharedSession?.session_name) {
        throw new Error('Sessão do WhatsApp não encontrada. Verifique a conexão.');
      }

      // Call reprocess edge function with sessionName
      const { data, error } = await supabase.functions.invoke('reprocess-thumbnail-media', {
        body: {
          sessionName: sharedSession.session_name,
          limit: 1,
          dryRun: false
        }
      });

      if (error) throw error;

      if (data?.recovered > 0) {
        toast.success('Mídia reprocessada com sucesso! Recarregue a página.');
      } else {
        toast.warning('Não foi possível recuperar a mídia original. O servidor pode não ter mais o arquivo.');
      }
    } catch (error) {
      console.error('Error reprocessing media:', error);
      toast.error('Erro ao reprocessar mídia');
    } finally {
      setReprocessing(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getIcon = () => {
    if (isImage) return <ImageIcon className="h-5 w-5" />;
    if (isVideo) return <Film className="h-5 w-5" />;
    if (isAudio) return <Music className="h-5 w-5" />;
    if (isPdf) return <FileText className="h-5 w-5" />;
    return <FileText className="h-5 w-5" />;
  };

  // Render placeholder for small/thumbnail files
  if (needsReprocess) {
    // If media is expired, show different UI
    if (mediaExpired) {
      return (
        <div className="my-2">
          <div className={`flex items-center gap-3 p-3 rounded-lg border ${
            isOutbound 
              ? 'border-primary-foreground/20 bg-primary-foreground/5' 
              : 'border-muted bg-muted/30'
          }`}>
            <div className={`p-2 rounded-full ${
              isOutbound 
                ? 'bg-primary-foreground/10 text-primary-foreground' 
                : 'bg-muted text-muted-foreground'
            }`}>
              <Clock className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${
                isOutbound ? 'text-primary-foreground' : ''
              }`}>
                {isImage ? 'Imagem' : isVideo ? 'Vídeo' : isAudio ? 'Áudio' : 'Arquivo'} expirado
              </p>
              <p className={`text-xs ${
                isOutbound ? 'text-primary-foreground/70' : 'text-muted-foreground'
              }`}>
                Mídia não disponível no WhatsApp ({daysSinceCreation} dias atrás)
              </p>
            </div>
            {onRequestResend && !isOutbound && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRequestResend}
                className="shrink-0"
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                Pedir reenvio
              </Button>
            )}
          </div>
        </div>
      );
    }
    
    // Media not expired - show retry button
    return (
      <div className="my-2">
        <div className={`flex items-center gap-3 p-3 rounded-lg border ${
          isOutbound 
            ? 'border-primary-foreground/20 bg-primary-foreground/5' 
            : 'border-muted bg-muted/30'
        }`}>
          <div className={`p-2 rounded-full ${
            isOutbound 
              ? 'bg-primary-foreground/10 text-primary-foreground' 
              : 'bg-amber-500/10 text-amber-600'
          }`}>
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium truncate ${
              isOutbound ? 'text-primary-foreground' : ''
            }`}>
              {isImage ? 'Imagem' : isVideo ? 'Vídeo' : isAudio ? 'Áudio' : 'Arquivo'} incompleto
            </p>
            <p className={`text-xs ${
              isOutbound ? 'text-primary-foreground/70' : 'text-muted-foreground'
            }`}>
              Apenas miniatura ({formatFileSize(attachment.file_size)}) - esperado &gt;{formatFileSize(minSize)}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReprocess}
            disabled={reprocessing}
            className={`shrink-0 ${
              isOutbound 
                ? 'text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/10' 
                : ''
            }`}
          >
            {reprocessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-1">{reprocessing ? 'Reprocessando...' : 'Tentar novamente'}</span>
          </Button>
        </div>
      </div>
    );
  }

  // Render image inline - auto-loaded with proper state handling
  if (isImage) {
    // 1. Se não tem nenhuma fonte disponível (ambas vazias)
    if (noMediaAvailable) {
      return (
        <div className="my-2">
          <div className={`flex items-center gap-2 p-2 rounded-lg ${
            isOutbound ? 'bg-primary-foreground/10' : 'bg-muted/50'
          }`}>
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              Mídia não foi salva — link expirado
            </span>
          </div>
        </div>
      );
    }

    // 2. Se ainda não tentou carregar, mostrar loading (useEffect vai disparar loadImage)
    if (!loadAttempted) {
      return (
        <div className="my-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Carregando...</span>
          </div>
        </div>
      );
    }

    // 3. Se tem imageUrl, renderizar imagem
    if (imageUrl) {
      return (
        <div className="my-2 space-y-2">
          <div className="rounded-lg overflow-hidden max-w-xs relative">
            {imageLoading && (
              <div className="flex items-center gap-2 py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-xs">Carregando imagem...</span>
              </div>
            )}
            <img 
              src={imageUrl} 
              alt={attachment.file_name}
              className={`w-full h-auto cursor-pointer hover:opacity-90 transition-opacity ${imageLoading ? 'opacity-0 absolute' : ''}`}
              onLoad={() => setImageLoading(false)}
              onClick={() => window.open(imageUrl, '_blank')}
              onError={handleImageError}
            />
          </div>
          {!imageLoading && usingFallback && hasExternalUrl && (
            <div className="flex items-center gap-1 text-xs text-warning">
              <Clock className="h-3 w-3" />
              <span>Mídia temporária (pode expirar)</span>
            </div>
          )}
          {!imageLoading && (
            <div className="flex items-center gap-2">
              {attachment.file_size > 0 && (
                <span className="text-xs opacity-70">{formatFileSize(attachment.file_size)}</span>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                className={`h-6 ${isOutbound ? 'text-primary-foreground hover:bg-primary-foreground/10' : ''}`}
              >
                <Download className="h-3 w-3 mr-1" />
                {isExternalOnly ? 'Abrir' : 'Baixar'}
              </Button>
            </div>
          )}
        </div>
      );
    }

    // 4. Se ambas as fontes falharam após tentativas
    if (storageUrlFailed && externalUrlFailed) {
      return (
        <div className="my-2">
          <div className={`flex items-center gap-2 p-2 rounded-lg ${
            isOutbound ? 'bg-primary-foreground/10' : 'bg-muted/50'
          }`}>
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              Falha ao carregar — mídia pode ter expirado
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setExternalUrlFailed(false);
                setStorageUrlFailed(false);
                setImageUrl(null);
                setLoadAttempted(false);
              }}
              className={`shrink-0 ${isOutbound ? 'text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/10' : ''}`}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Tentar novamente
            </Button>
          </div>
        </div>
      );
    }

    // 5. Se só external falhou mas ainda não tinha storage para tentar
    if (externalUrlFailed && !hasStoragePath) {
      return (
        <div className="my-2">
          <div className={`flex items-center gap-2 p-2 rounded-lg ${
            isOutbound ? 'bg-primary-foreground/10' : 'bg-muted/50'
          }`}>
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              Mídia temporária expirada
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setExternalUrlFailed(false);
                setLoadAttempted(false);
              }}
              className={`shrink-0 ${isOutbound ? 'text-primary-foreground border-primary-foreground/20 hover:bg-primary-foreground/10' : ''}`}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Tentar novamente
            </Button>
          </div>
        </div>
      );
    }

    // 6. Fallback: ainda carregando
    return (
      <div className="my-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Carregando...</span>
        </div>
      </div>
    );
  }

  // Render PDF with inline viewer option
  if (isPdf) {
    const handleViewPdf = async () => {
      if (fileUrl) {
        window.open(fileUrl, '_blank');
        return;
      }
      
      setLoading(true);
      try {
        const { data, error } = await supabase.storage
          .from('whatsapp-media')
          .createSignedUrl(attachment.storage_path, 3600);

        if (error) throw error;
        setFileUrl(data.signedUrl);
        window.open(data.signedUrl, '_blank');
      } catch (error) {
        console.error('Error loading PDF:', error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="my-2 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleViewPdf}
          disabled={loading}
className={`${isOutbound ? 'bg-primary-foreground/15 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/25' : ''}`}
        >
          {getIcon()}
          <span className="ml-2">{loading ? 'Carregando...' : 'Ver PDF'}</span>
          <span className="ml-2 text-xs opacity-70">({formatFileSize(attachment.file_size)})</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          disabled={loading}
          className={`${isOutbound ? 'bg-primary-foreground/15 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/25' : ''}`}
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Render audio with inline player
  if (isAudio) {
    const handleViewAudio = async () => {
      if (fileUrl) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase.storage
          .from('whatsapp-media')
          .createSignedUrl(attachment.storage_path, 3600);

        if (error) throw error;
        setFileUrl(data.signedUrl);
      } catch (error) {
        console.error('Error loading audio:', error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="my-2">
        {!fileUrl ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewAudio}
            disabled={loading}
            className={`${isOutbound ? 'bg-primary-foreground/15 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/25' : ''}`}
          >
            {getIcon()}
            <span className="ml-2">{loading ? 'Carregando...' : 'Reproduzir Áudio'}</span>
            <span className="ml-2 text-xs opacity-70">({formatFileSize(attachment.file_size)})</span>
          </Button>
        ) : (
          <div className="space-y-2">
            <audio controls src={fileUrl} className="max-w-xs" />
            <div className="flex items-center gap-2">
              <span className="text-xs opacity-70">{formatFileSize(attachment.file_size)}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                className={`h-6 ${isOutbound ? 'bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/25' : ''}`}
              >
                <Download className="h-3 w-3 mr-1" />
                Baixar
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render video with inline player
  if (isVideo) {
    const handleViewVideo = async () => {
      if (fileUrl) return;
      
      setLoading(true);
      try {
        const { data, error } = await supabase.storage
          .from('whatsapp-media')
          .createSignedUrl(attachment.storage_path, 3600);

        if (error) throw error;
        setFileUrl(data.signedUrl);
      } catch (error) {
        console.error('Error loading video:', error);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="my-2">
        {!fileUrl ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleViewVideo}
            disabled={loading}
            className={`${isOutbound ? 'bg-primary-foreground/15 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/25' : ''}`}
          >
            {getIcon()}
            <span className="ml-2">{loading ? 'Carregando...' : 'Ver Vídeo'}</span>
            <span className="ml-2 text-xs opacity-70">({formatFileSize(attachment.file_size)})</span>
          </Button>
        ) : (
          <div className="space-y-2">
            <video 
              controls 
              src={fileUrl} 
              className="max-w-xs rounded-lg"
              onError={() => {
                console.error('Failed to load video');
                setFileUrl(null);
              }}
            />
            <div className="flex items-center gap-2">
              <span className="text-xs opacity-70">{formatFileSize(attachment.file_size)}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                className={`h-6 ${isOutbound ? 'bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/25' : ''}`}
              >
                <Download className="h-3 w-3 mr-1" />
                Baixar
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Render other file types as download buttons
  return (
    <div className="my-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleDownload}
        disabled={loading}
        className={`${isOutbound ? 'bg-primary-foreground/15 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/25' : ''}`}
      >
        {getIcon()}
        <span className="ml-2 truncate max-w-[200px]">{attachment.file_name}</span>
        <Download className="h-4 w-4 ml-2" />
        <span className="ml-2 text-xs opacity-70">({formatFileSize(attachment.file_size)})</span>
      </Button>
    </div>
  );
}